<script setup lang="ts">
/* =============================================================
   AUTH LAYOUT
   =============================================================
   Full-screen split used by sign-in and sign-up: a dark brand
   panel on one side, the form on the other. Below the mobile
   breakpoint the brand panel collapses to a slim header.

   All copy comes from app.config — the layout itself says nothing
   product-specific.
   ============================================================= */

import { RouterView } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Logo from '@/components/Logo.vue'
import { appConfig } from '@/config/app.config'
import type { IconName } from '@/components/icons'

const highlights: { icon: IconName; text: string }[] = [
  { icon: 'anchor', text: 'Digitise a quay in minutes — limits, amenities, pricing, availability.' },
  { icon: 'search', text: 'Search and compare berths that actually fit your vessel.' },
  { icon: 'bookings', text: 'Requests, confirmations and receipts in one place.' },
]

const year = new Date().getFullYear()
</script>

<template>
  <div class="wrap">
    <aside class="brand invert">
      <!-- Decorative river bands. -->
      <svg class="waves" viewBox="0 0 600 800" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 620c150 0 150-90 300-90s150 90 300 90v180H0z" class="wave1" />
        <path d="M0 700c150 0 150-70 300-70s150 70 300 70v100H0z" class="wave2" />
      </svg>

      <div class="brandTop">
        <a :href="appConfig.urls.marketing" class="brandLogo">
          <Logo :size="36" />
        </a>
      </div>

      <div class="brandBody">
        <h2 class="brandTitle">{{ appConfig.tagline }}</h2>
        <p class="brandText">{{ appConfig.description }}</p>

        <ul class="highlights">
          <li v-for="item in highlights" :key="item.text" class="highlight">
            <span class="highlightIcon">
              <Icon :name="item.icon" :size="16" />
            </span>
            {{ item.text }}
          </li>
        </ul>
      </div>

      <p class="brandFoot">© {{ year }} {{ appConfig.org }}</p>
    </aside>

    <main class="panel">
      <div class="form">
        <RouterView />
      </div>

      <footer class="panelFoot">
        <a :href="appConfig.urls.terms">Terms</a>
        <span aria-hidden="true">·</span>
        <a :href="appConfig.urls.privacy">Privacy</a>
        <span aria-hidden="true">·</span>
        <a :href="`mailto:${appConfig.supportEmail}`">{{ appConfig.supportEmail }}</a>
      </footer>
    </main>
  </div>
</template>

<style scoped>
.wrap {
  display: grid;
  grid-template-columns: var(--auth-panel-width) minmax(0, 1fr);
  min-height: 100dvh;
}

/* --- brand panel ------------------------------------------------ */

.brand {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-lg);
  padding: var(--space-xl) clamp(var(--space-lg), 5vw, var(--space-2xl));
  overflow: hidden;
  background-image: linear-gradient(160deg, var(--rv-navy-900), var(--rv-navy-950) 60%);
}

.waves {
  position: absolute;
  inset-block-end: 0;
  inset-inline: 0;
  width: 100%;
  height: 55%;
  pointer-events: none;
}

.wave1 {
  fill: color-mix(in srgb, var(--rv-teal-500) 14%, transparent);
}

.wave2 {
  fill: color-mix(in srgb, var(--rv-teal-400) 9%, transparent);
}

.brandTop,
.brandBody,
.brandFoot {
  position: relative;
  z-index: var(--z-base);
}

.brandLogo {
  display: inline-flex;
  text-decoration: none;
}

.brandBody {
  max-width: 34ch;
}

.brandTitle {
  font-size: var(--text-xl);
  line-height: var(--leading-snug);
  color: var(--color-heading);
  text-wrap: balance;
}

.brandText {
  margin-top: var(--space-2xs);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.highlights {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  margin-top: var(--space-lg);
  padding: 0;
  list-style: none;
}

.highlight {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2xs);
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
  color: var(--color-text-faint);
}

.highlightIcon {
  display: grid;
  place-items: center;
  flex: none;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--color-accent);
  background-color: var(--color-accent-soft);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
}

.brandFoot {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

/* --- form side --------------------------------------------------- */

.panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-lg);
  padding: var(--space-xl) var(--content-pad-x);
  background-color: var(--color-bg);
}

.form {
  width: 100%;
  max-width: 25rem;
}

.panelFoot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-2xs);
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.panelFoot a {
  color: var(--color-text-muted);
  text-decoration: none;
}

.panelFoot a:hover {
  color: var(--color-accent);
  text-decoration: underline;
}

/* --- stacked layout ---------------------------------------------- */

@media (max-width: 56.25rem) {
  .wrap {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
  }

  .brand {
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-md) var(--space-lg);
  }

  .brandBody {
    max-width: none;
  }

  .brandTitle {
    font-size: var(--text-lg);
  }

  .highlights,
  .brandFoot,
  .waves {
    display: none;
  }

  .panel {
    justify-content: flex-start;
    padding-top: var(--space-lg);
  }
}
</style>
